import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Pill.css';

export interface PillProps extends BaseProps {
  children?: ReactNode;
  /** Adds a × button that fires `onRemove`. */
  withRemoveButton?: boolean;
  onRemove?: () => void;
  /**
   * Accessible name for the remove button. Defaults to "Remove", which is the
   * only English string this component emits: both consoles are translated, so
   * a caller that names the thing being removed ("Remove tag vip") gives a
   * screen-reader user something far more useful than a row of identical
   * "Remove" buttons.
   */
  removeLabel?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/** Compact pill, often used for tags / multi-select chips. */
export const Pill = forwardRef(function Pill(
  { children, withRemoveButton, onRemove, removeLabel = 'Remove', size = 'sm', ...rest }: PillProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const base = useBaseAttrs('rr-pill', rest, [`rr-pill--size-${size}`]);
  const { dom } = splitBaseProps(rest);
  return (
    <span {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      <span className="rr-pill__label">{children}</span>
      {withRemoveButton ? (
        <button
          type="button"
          className="rr-pill__remove"
          aria-label={removeLabel}
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
        >
          ×
        </button>
      ) : null}
    </span>
  );
});
